/**
 * RankedListTable — compact sortable ranked list for drivers.
 * Used for both Top Schedule Drivers (activities) and Top WBS at Risk.
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { ActivityDriver, WBSDriver, ActivityFlag } from '@/types/cockpit';

// ---------- Activity Drivers ----------

export interface ActivityDriverTableProps {
  drivers: ActivityDriver[];
  className?: string;
}

const FLAG_VARIANT: Record<ActivityFlag, 'critical' | 'warn' | 'outline' | 'default'> = {
  critical: 'critical',
  'near-critical': 'warn',
  'negative-float': 'critical',
  constrained: 'warn',
  'finishing-soon': 'warn',
  'long-duration': 'outline',
};

const FLAG_LABEL: Record<ActivityFlag, string> = {
  critical: 'Critical',
  'near-critical': 'Near-critical',
  'negative-float': 'Negative Float',
  constrained: 'Constrained',
  'finishing-soon': 'Finishing soon',
  'long-duration': 'Long duration',
};

function truncate(s: string, max = 32): string {
  return s.length <= max ? s : s.slice(0, max - 1) + '…';
}

export const ActivityDriverTable: React.FC<ActivityDriverTableProps> = ({ drivers, className }) => {
  const navigate = useNavigate();
  const [sortAsc, setSortAsc] = useState(false);

  const sorted = useMemo(() => {
    const copy = [...drivers];
    copy.sort((a, b) => (sortAsc ? a.score - b.score : b.score - a.score));
    return copy;
  }, [drivers, sortAsc]);

  if (drivers.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4 text-center">No activity drivers computed.</p>
    );
  }

  return (
    <div className={cn('rounded-lg border bg-card overflow-hidden', className)}>
      <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
        <table className="w-full text-[11px]">
          <thead className="sticky top-0 bg-card z-10">
            <tr className="border-b text-muted-foreground">
              <th className="text-left font-medium px-3 py-2">Activity</th>
              <th className="text-left font-medium px-3 py-2">WBS</th>
              <th className="text-left font-medium px-3 py-2 whitespace-nowrap">Finish</th>
              <th className="text-right font-medium px-3 py-2 whitespace-nowrap">TF (d)</th>
              <th className="text-left font-medium px-3 py-2">Flags</th>
              <th
                className="text-right font-medium px-3 py-2 cursor-pointer select-none whitespace-nowrap hover:text-foreground"
                onClick={() => setSortAsc((p) => !p)}
                title="Click to toggle sort"
              >
                Score {sortAsc ? '↑' : '↓'}
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((d) => (
              <tr
                key={d.id}
                className="border-b last:border-b-0 hover:bg-muted/40 cursor-pointer transition-colors"
                onClick={() => navigate(`/app/activities?activity_id=${d.id}`)}
              >
                <td className="px-3 py-1.5 font-medium text-foreground max-w-[180px] truncate" title={d.name}>
                  {truncate(d.name)}
                </td>
                <td className="px-3 py-1.5 text-muted-foreground max-w-[100px] truncate" title={d.wbs}>
                  {d.wbs || '—'}
                </td>
                <td className="px-3 py-1.5 text-muted-foreground whitespace-nowrap">
                  {d.finish ?? '—'}
                </td>
                <td className="px-3 py-1.5 text-right whitespace-nowrap">
                  {d.tfDays !== null ? (
                    <span className={cn(d.tfDays < 0 ? 'text-red-500 font-medium' : 'text-muted-foreground')}>
                      {d.tfDays}
                    </span>
                  ) : '—'}
                </td>
                <td
                  className="px-3 py-1.5"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex flex-wrap gap-1">
                    {d.flags.slice(0, 3).map((f) => (
                      <Badge key={f} variant={FLAG_VARIANT[f]} size="sm">
                        {FLAG_LABEL[f] ?? f}
                      </Badge>
                    ))}
                    {d.flags.length > 3 && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 rounded"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Badge variant="outline" size="sm">
                              +{d.flags.length - 3}
                            </Badge>
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="min-w-[180px]">
                          <div className="px-2 py-1.5 text-[11px] font-medium text-muted-foreground border-b border-border mb-1">
                            Additional flags
                          </div>
                          {d.flags.slice(3).map((f) => (
                            <DropdownMenuItem key={f} disabled className="text-xs cursor-default">
                              {FLAG_LABEL[f] ?? f}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </td>
                <td className="px-3 py-1.5 text-right font-semibold text-foreground">
                  {d.score}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------- WBS Drivers ----------

export interface WBSDriverTableProps {
  drivers: WBSDriver[];
  className?: string;
}

export const WBSDriverTable: React.FC<WBSDriverTableProps> = ({ drivers, className }) => {
  const navigate = useNavigate();
  const [sortAsc, setSortAsc] = useState(false);

  const sorted = useMemo(() => {
    const copy = [...drivers];
    copy.sort((a, b) => (sortAsc ? a.riskScore - b.riskScore : b.riskScore - a.riskScore));
    return copy;
  }, [drivers, sortAsc]);

  if (drivers.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4 text-center">No WBS risk data computed.</p>
    );
  }

  return (
    <div className={cn('rounded-lg border bg-card overflow-hidden', className)}>
      <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
        <table className="w-full text-[11px]">
          <thead className="sticky top-0 bg-card z-10">
            <tr className="border-b text-muted-foreground">
              <th className="text-left font-medium px-3 py-2">WBS</th>
              <th className="text-right font-medium px-3 py-2 whitespace-nowrap">Critical</th>
              <th className="text-right font-medium px-3 py-2 whitespace-nowrap">Negative Float</th>
              <th className="text-right font-medium px-3 py-2 whitespace-nowrap">Constrained</th>
              <th
                className="text-right font-medium px-3 py-2 cursor-pointer select-none whitespace-nowrap hover:text-foreground"
                onClick={() => setSortAsc((p) => !p)}
                title="Click to toggle sort"
              >
                Risk {sortAsc ? '↑' : '↓'}
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((d) => (
              <tr
                key={d.id}
                className="border-b last:border-b-0 hover:bg-muted/40 cursor-pointer transition-colors"
                onClick={() => navigate(`/app/activities?wbs_id=${d.id}`)}
              >
                <td className="px-3 py-1.5 font-medium text-foreground max-w-[200px] truncate" title={d.name}>
                  <span className="text-muted-foreground mr-1">{d.code}</span>
                  {truncate(d.name, 24)}
                </td>
                <td className="px-3 py-1.5 text-right">
                  <span className={cn(d.criticalCount > 0 ? 'text-red-500 font-medium' : 'text-muted-foreground')}>
                    {d.criticalCount}
                  </span>
                </td>
                <td className="px-3 py-1.5 text-right">
                  <span className={cn(d.negFloatCount > 0 ? 'text-red-500 font-medium' : 'text-muted-foreground')}>
                    {d.negFloatCount}
                  </span>
                </td>
                <td className="px-3 py-1.5 text-right">
                  <span className={cn(d.constraintCount > 0 ? 'text-amber-500 font-medium' : 'text-muted-foreground')}>
                    {d.constraintCount}
                  </span>
                </td>
                <td className="px-3 py-1.5 text-right font-semibold text-foreground">
                  {d.riskScore}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
